import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, TrendingUp, ArrowRight, Users, MapPin } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExperiencesPage = () => {
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await axios.get(`${API}/packages`);
        setPackages(response.data);
      } catch (error) {
        console.error('Error fetching packages:', error);
      }
    };
    fetchPackages();
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  return (
    <div className="bg-[#f5f3ee]" data-testid="experiences-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-24" data-testid="experiences-hero">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_517a97fe-c059-48a3-bb5f-cd3bbb5bb437/artifacts/w8l8adx3_WhatsApp%20Image%202026-02-08%20at%2023.28.43.jpeg"
            alt="Padel courts at sunset"
            className="w-full h-full object-cover"
            style={{ objectPosition: '50% 85%' }}
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-5 py-2 border border-[#f5f3ee]/40 text-[#f5f3ee] text-xs tracking-[0.3em] uppercase mb-8">
              Our Packages
            </span>
            <h1 className="font-heading font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#f5f3ee] mb-6">
              Padel Experiences<br />
              <span className="italic">For Everyone</span>
            </h1>
            <p className="text-lg text-[#f5f3ee]/80 max-w-xl mx-auto font-light">
              From weekend escapes to intensive camps, find the perfect padel holiday for you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="py-24 md:py-32" data-testid="packages-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="text-xs font-medium tracking-[0.3em] uppercase text-[#8ca87c] mb-6 block">
              Choose Your Adventure
            </span>
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-[#414b3b] mb-6">
              Our Packages
            </h2>
            <p className="text-base text-[#414b3b]/60 max-w-2xl mx-auto font-light">
              Each package is carefully crafted to deliver the perfect balance of padel, 
              relaxation, and adventure.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white overflow-hidden group flex flex-col h-full"
                data-testid={`package-card-${index}`}
              >
                {/* Package Image */}
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={pkg.image_url}
                    alt={pkg.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                    {pkg.price_from ? (
                      <span className="px-4 py-2 bg-white text-[#414b3b] text-sm font-light tracking-wider">
                        From €{pkg.price_from}
                      </span>
                    ) : (
                      <span className="px-4 py-2 bg-white text-[#414b3b] text-sm font-light tracking-wider">
                        Under Request
                      </span>
                    )}
                    {pkg.training_type && (
                      <span className="px-4 py-2 bg-[#8ca87c] text-white text-sm font-light tracking-wider">
                        {pkg.training_type}
                      </span>
                    )}
                    {pkg.group_size && (
                      <span className="px-4 py-2 bg-[#414b3b] text-white text-sm font-light tracking-wider">
                        {pkg.group_size}
                      </span>
                    )}
                  </div>
                </div>

                {/* Package Content */}
                <div className="p-8 flex flex-col flex-grow">
                  <h3 className="font-heading text-2xl text-[#414b3b] mb-3">
                    {pkg.name}
                  </h3>
                  <p className="text-[#414b3b]/60 mb-6 font-light leading-relaxed min-h-[72px]">
                    {pkg.description}
                  </p>

                  {/* Key Details */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="bg-[#f5f3ee] p-3 text-center">
                      <span className="text-[#414b3b]/50 text-xs tracking-wider uppercase block mb-1">Location</span>
                      <span className="text-[#414b3b] font-heading text-sm">{pkg.destination}</span>
                    </div>
                    <div className="bg-[#f5f3ee] p-3 text-center">
                      <span className="text-[#414b3b]/50 text-xs tracking-wider uppercase block mb-1">Duration</span>
                      <span className="text-[#414b3b] font-heading text-sm">{pkg.duration}</span>
                    </div>
                    <div className="bg-[#f5f3ee] p-3 text-center">
                      <span className="text-[#414b3b]/50 text-xs tracking-wider uppercase block mb-1">Level</span>
                      <span className="text-[#414b3b] font-heading text-sm">{pkg.level}</span>
                    </div>
                    <div className="bg-[#f5f3ee] p-3 text-center">
                      <span className="text-[#414b3b]/50 text-xs tracking-wider uppercase block mb-1">Availability</span>
                      <span className="text-[#8ca87c] font-heading text-sm">All Year</span>
                    </div>
                  </div>

                  <Link
                    to={`/experiences/${pkg.id}`}
                    className="w-full h-12 bg-[#414b3b] text-[#f5f3ee] font-light text-sm tracking-[0.1em] uppercase flex items-center justify-center gap-2 transition-all duration-300 hover:bg-[#8ca87c] mt-auto"
                    data-testid={`view-package-${index}`}
                  >
                    View Package
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Packages */}
      <section className="py-24 md:py-32 bg-white" data-testid="custom-packages">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeInUp}>
              <span className="text-xs font-medium tracking-[0.3em] uppercase text-[#8ca87c] mb-6 block">
                Tailored Experiences
              </span>
              <h2 className="font-heading font-light text-3xl sm:text-4xl text-[#414b3b] mb-6">
                Need Something<br />
                <span className="italic text-[#414b3b]/50">Different?</span>
              </h2>
              <p className="text-base text-[#414b3b]/60 mb-8 font-light leading-relaxed">
                We specialize in creating custom padel experiences for groups, corporate teams, 
                and special occasions. Tell us your vision, and we'll make it happen.
              </p>
              
              <ul className="space-y-4 mb-10">
                {[
                  'Corporate team building & retreats',
                  'Private group bookings',
                  'Tournament organization',
                  'Custom duration & activities',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-4">
                    <span className="text-[#8ca87c]">✦</span>
                    <span className="text-[#414b3b]/70 font-light">{item}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/contact"
                className="inline-flex items-center gap-3 h-14 px-8 bg-[#414b3b] text-[#f5f3ee] font-light text-sm tracking-[0.15em] uppercase transition-all duration-300 hover:bg-[#8ca87c]"
                data-testid="custom-inquiry-btn"
              >
                <Users size={18} />
                Inquire About Custom Packages
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="overflow-hidden aspect-[4/3]">
                <img
                  src="https://customer-assets.emergentagent.com/job_517a97fe-c059-48a3-bb5f-cd3bbb5bb437/artifacts/qjl8q4md_WhatsApp%20Image%202026-02-07%20at%2012.21.45%20%281%29.jpeg"
                  alt="Padel group experience"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#8ca87c]" data-testid="experiences-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading font-light text-3xl sm:text-4xl text-[#414b3b] mb-6">
              Ready to Book?
            </h2>
            <p className="text-base text-[#414b3b]/70 mb-10 font-light">
              Choose your perfect package and start your padel adventure in the Algarve.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="h-14 px-10 bg-[#414b3b] text-[#f5f3ee] font-light text-sm tracking-[0.15em] uppercase flex items-center justify-center gap-3 transition-all duration-300 hover:bg-[#333d2e]"
                data-testid="experiences-book-btn"
              >
                Book Now
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/how-it-works"
                className="h-14 px-10 border border-[#414b3b] text-[#414b3b] font-light text-sm tracking-[0.15em] uppercase flex items-center justify-center transition-all duration-300 hover:bg-[#414b3b] hover:text-[#f5f3ee]"
                data-testid="experiences-how-btn"
              >
                How It Works
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ExperiencesPage;
